import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./StudentDashboard.css";
import "../../styles/dashboard-shell.css";
import {
  User,
  MessageSquare,
  Calendar,
  BarChart3,
  BookOpen,
  Award,
  Settings,
  TrendingUp,
  Clock,
  Play,
  CheckCircle,
  Target,
  Star,
  Trophy,
  Brain,
  FileText,
  Activity,
  Zap,
  AlertCircle,
  Loader,
  Menu,
  X,
  CalendarDays,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import api, { refreshAccessToken } from "../../services/api";
import Messaging from "../../components/Dashboard/Messaging/Messaging";
import PointsWidget from "../../components/Dashboard/PointsWidget/PointsWidget";
import LearningStreak from "../../components/LearningStreak/LearningStreak";
import useLearnerSnapshot from "../../hooks/useLearnerSnapshot";
import AnnouncementBanner from "../../components/AnnouncementBanner/AnnouncementBanner";
import ReadingTimetable from "../../components/ReadingTimetable/ReadingTimetable";
import { getApiBaseUrl } from "../../services/apiBase.js";

// ==================== CONSTANTS ====================
const API_BASE_URL = getApiBaseUrl();
const REFRESH_INTERVAL = 300000; // 5 minutes (reduced excessive refreshing)
const CROSS_TAB_CHECK_INTERVAL = 30000; // 30 seconds (reduced from 5 seconds)

const AGENT_PAGES = [
  {
    name: "Courses",
    icon: BookOpen,
    description: "Learn chapter by chapter",
    color: "#0B1B3F",
    route: "/courses",
  },
  {
    name: "AI Tutor",
    icon: Brain,
    description: "Get personalized help",
    color: "#0B1B3F",
    route: "/agents/tutor",
  },
  {
    name: "Quiz Generator",
    icon: Target,
    description: "Practice with smart quizzes",
    color: "#D9950F",
    route: "/agents/quiz",
  },
  {
    name: "Curriculum Viewer",
    icon: BookOpen,
    description: "Browse curriculum content",
    color: "#2A4F9E",
    route: "/agents/curriculum-viewer",
  },
  {
    name: "Summarizer",
    icon: FileText,
    description: "Summarize study materials",
    color: "#0B1B3F",
    route: "/agents/summarize",
  },
  {
    name: "Progress Monitor",
    icon: BarChart3,
    description: "Track your progress",
    color: "#D9950F",
    route: "/agents/progress",
  },
  {
    name: "Analytics",
    icon: TrendingUp,
    description: "View learning analytics",
    color: "#2A4F9E",
    route: "/agents/analytics",
  },
  {
    name: "Learning Insights",
    icon: Star,
    description: "View quiz history and AI feedback",
    color: "#0B1B3F",
    route: "/agents/feedback",
  },
  {
    name: "Learning Path",
    icon: Zap,
    description: "Get personalized recommendations",
    color: "#D9950F",
    route: "/agents/recommendations",
  },
];

// ==================== UTILITY FUNCTIONS ====================
const getAuthToken = () => {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("access_token")
  );
};

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => toCamelCase(v));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [key.replace(/_([a-z])/g, (g) => g[1].toUpperCase())]: toCamelCase(
          obj[key]
        ),
      }),
      {}
    );
  }
  return obj;
};

const formatDate = (dateString) => {
  if (!dateString) return "Recently";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (dateString) => {
  if (!dateString) return "--:--";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ==================== SESSION MANAGEMENT ====================
// Add session timeout prevention
let sessionTimeoutId = null;
let lastActivityTime = Date.now();

const resetSessionTimeout = () => {
  lastActivityTime = Date.now();
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }
  // Auto logout after 2 hours of inactivity
  sessionTimeoutId = setTimeout(() => {
    console.log("🕐 Session timeout - logging out");
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  }, 2 * 60 * 60 * 1000); // 2 hours
};

// Track user activity to prevent unnecessary logouts
const trackActivity = () => {
  const now = Date.now();
  if (now - lastActivityTime > 60000) {
    // Only reset if more than 1 minute since last activity
    resetSessionTimeout();
  }
};

// ==================== CUSTOM HOOKS ====================
const useApi = () => {
  const navigate = useNavigate();

  const apiCall = useCallback(
    async (endpoint, retries = 1) => {
      trackActivity(); // Track user activity

      const token = getAuthToken();

      if (!token) {
        navigate("/login");
        throw new Error("No authentication token found");
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 401) {
            // Renew the session once, then retry; only sign out if renewal fails
            if (retries > 0 && (await refreshAccessToken())) {
              return apiCall(endpoint, 0);
            }
            ["access_token", "refresh_token", "token_type", "role"].forEach((k) => {
              localStorage.removeItem(k);
              sessionStorage.removeItem(k);
            });
            navigate("/login");
            throw new Error("Session expired");
          }

          // For server errors, retry once before failing
          if (response.status >= 500 && retries > 0) {
            console.log(`🔄 Server error ${response.status}, retrying...`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return apiCall(endpoint, retries - 1);
          }

          throw new Error(`API Error: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("⏰ Request timeout");
          throw new Error("Request timeout - please check your connection");
        }
        throw error;
      }
    },
    [navigate]
  );

  return { apiCall };
};

const useDashboardData = () => {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
  });

  const { apiCall } = useApi();
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);

  const fetchData = useCallback(
    async (force = false) => {
      const now = Date.now();

      // Prevent excessive API calls - minimum 30 seconds between fetches unless forced
      if (!force && now - lastFetchTime < 30000) {
        console.log("🚫 Skipping fetch - too soon since last fetch");
        return state.data;
      }

      // If we've had consecutive errors, increase delay between retries
      if (consecutiveErrors >= 3 && !force) {
        console.log("🚫 Skipping fetch - too many consecutive errors");
        return state.data;
      }

      try {
        console.log(
          "🔄 Dashboard fetchData called at:",
          new Date().toLocaleTimeString()
        );
        setState((prev) => ({ ...prev, loading: true, error: null }));
        setLastFetchTime(now);

        // Parallel API calls with better error handling
        const [profile, points, progress, achievements, streak, leaderboard] =
          await Promise.all([
            apiCall("/profile/me"),
            apiCall("/points/me").catch((err) => {
              console.warn("Points API failed:", err.message);
              return null;
            }),
            apiCall("/profile/me/progress").catch((err) => {
              console.warn("Progress API failed:", err.message);
              return null;
            }),
            apiCall("/profile/me/achievements").catch((err) => {
              console.warn("Achievements API failed:", err.message);
              return null;
            }),
            apiCall(
              "/api/streak/" +
                (localStorage.getItem("student_id") ||
                  localStorage.getItem("user_id"))
            ).catch((err) => {
              console.warn("Streak API failed:", err.message);
              return null;
            }),
            apiCall("/points/leaderboard?limit=10").catch((err) => {
              console.warn("Leaderboard API failed:", err.message);
              return null;
            }),
          ]);

        // Store IDs
        if (profile.user_id) localStorage.setItem("user_id", profile.user_id);
        if (profile.student_id)
          localStorage.setItem("student_id", profile.student_id);

        // Build dashboard data
        const pointsData = points?.success ? points.data : null;
        const progressData = progress?.success ? progress.progress : null;
        const achievementsData = achievements?.success
          ? achievements.achievements
          : null;
        const streakData = streak || { current_streak: 0 };
        const leaderboardData = leaderboard?.success ? leaderboard : null;

        const totalPoints = pointsData?.points?.total || 0;
        const currentLevel = Math.max(1, Math.floor(totalPoints / 100));
        const xpProgress = totalPoints % 100;

        // Debug progress data
        console.log("📊 Dashboard Progress Data:", progressData);
        console.log("📊 Quiz Results Count:", progressData?.quiz_results);
        console.log("📊 Quiz Attempts Count:", progressData?.quiz_attempts);
        console.log("📊 Average Score:", progressData?.average_score);
        console.log(
          "📊 Recent Activity Count:",
          progressData?.recent_activity?.length || 0
        );

        const dashboardData = {
          studentProfile: {
            username: profile.username,
            name: profile.name
              ? profile.surname
                ? `${profile.name} ${profile.surname}`
                : profile.name
              : profile.username || "Student",
            email: profile.email,
            grade: profile.grade || "Not set",
            studentId: profile.student_id,
            userId: profile.user_id,
            school: profile.school || "Not specified",
            bio: profile.bio || "",
            currentSubject: "General",
            preferredTime: profile.preferred_time || "Not set",
            learningStyle: profile.learning_style || "Not set",
            notifications: profile.notifications || "Email + Push",
          },
          progressStats: {
            completedLessons: progressData?.quiz_results || 0,
            totalLessons: 40,
            completedQuizzes: progressData?.quiz_results || 0,
            totalQuizzes: 40,
            quizCompletionPercentage:
              progressData?.quiz_attempts > 0
                ? Math.round(
                    (progressData.quiz_results / progressData.quiz_attempts) *
                      100
                  )
                : 0,
            points: totalPoints,
            xp: totalPoints,
            level: currentLevel,
            xpProgress: xpProgress,
            xpNeeded: 100,
            xpForNextLevel: (currentLevel + 1) * 100,
            xpPercentage: Math.round(xpProgress),
            streak:
              streakData?.current_streak || streakData?.currentStreak || 0,
            averageScore: progressData?.average_score || 0,
          },
          achievements: {
            badges: achievementsData?.achievements || [],
            totalBadges: achievementsData?.achievements?.length || 0,
            level: achievementsData?.level || 1,
            achievements: achievementsData?.achievements || [],
          },
          recentActivity: progressData?.recent_activity
            ? progressData.recent_activity.map((activity) => ({
                type: activity.type,
                description: activity.type,
                timestamp: activity.date,
                points: activity.score,
                subject: activity.subject,
                topic: activity.topic,
              }))
            : [],
          messages: {
            unreadCount: 0,
            recentMessages: [],
          },
          notifications: {
            unreadCount: 0,
            items: [],
          },
          upcomingSessions: [],
          learningPath: [],
          leaderboard: {
            userRank: leaderboardData?.current_user_rank || null,
            totalStudents: leaderboardData?.pagination?.total_count || 0,
            topStudents: leaderboardData?.leaderboard || [],
          },
          pointsData: pointsData,
        };

        setState({ data: dashboardData, loading: false, error: null });

        // Reset consecutive error count on successful fetch
        setConsecutiveErrors(0);

        // Fetch agent usage
        if (profile.student_id) {
          fetchAgentUsage(profile.student_id);
        }
      } catch (error) {
        console.error("❌ Dashboard fetch error:", error);
        setConsecutiveErrors((prev) => prev + 1);
        setState({ data: null, loading: false, error: error.message });
      }
    },
    [apiCall]
  );

  const fetchAgentUsage = useCallback(
    async (studentId) => {
      try {
        const [usage, metrics] = await Promise.all([
          apiCall(`/agent-usage/student/${studentId}`).catch(() => ({
            agents: [],
          })),
          apiCall(`/agent-usage/metrics/student/${studentId}`).catch(() => ({
            totalInteractions: 0,
            totalInteractionTime: 0,
            subjectsEngaged: [],
            topicsCovered: [],
            agentUsageSummary: {},
          })),
        ]);

        setState((prev) => ({
          ...prev,
          data: {
            ...prev.data,
            agentUsage: toCamelCase(usage),
            agentMetrics: toCamelCase(metrics),
          },
        }));
      } catch (error) {
        console.error("Agent usage fetch failed:", error);
      }
    },
    [apiCall]
  );

  return { ...state, refetch: fetchData };
};

// ==================== SUB-COMPONENTS ====================
const lagosHour = () => {
  try {
    return Number(
      new Intl.DateTimeFormat("en-GB", { hour: "numeric", hourCycle: "h23", timeZone: "Africa/Lagos" }).format(new Date())
    );
  } catch {
    return new Date().getHours();
  }
};

const greeting = () => {
  const h = lagosHour();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const WelcomeSection = ({ studentProfile, progressStats }) => {
  const streak = progressStats?.streak || 0;
  const firstName = (studentProfile?.name || "Student").split(" ")[0];
  const xp = progressStats?.xpProgress || 0;
  return (
    <div className="welcome-section ov-welcome">
      <div className="welcome-text">
        <h2>
          {greeting()}, {firstName} 👋
        </h2>
        <p>
          {streak > 0
            ? `🔥 You're on a ${streak}-day learning streak — keep it going!`
            : "Learn something for just 10 minutes today to start your streak 🔥"}
        </p>
      </div>
      <div className="ov-level">
        <div className="ov-level-top">
          <span className="level-badge">Level {progressStats?.level || 1}</span>
          <span className="ov-points">
            <Zap size={16} /> {progressStats?.points || 0} points
          </span>
        </div>
        <div className="ov-xp" role="img" aria-label={`${xp} of 100 points to the next level`}>
          <div className="ov-xp-fill" style={{ width: `${Math.min(100, xp)}%` }} />
        </div>
        <small>{100 - xp} points to Level {(progressStats?.level || 1) + 1}</small>
      </div>
    </div>
  );
};

const TodayCard = ({ onNavigate, onAgentClick }) => {
  const [timetable, setTimetable] = useState(null);
  const courseNavigate = useNavigate();
  const [courseContinue, setCourseContinue] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/courses/continue")
      .then((res) => !cancelled && setCourseContinue(res.data?.continue || null))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/timetable")
      .then((res) => !cancelled && setTimetable(res.data))
      .catch(() => !cancelled && setTimetable({ blocks: [] }));
    return () => {
      cancelled = true;
    };
  }, []);

  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };
  const fmt = (t) => {
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")}${h >= 12 ? "pm" : "am"}`;
  };

  let todays = [];
  let next = null;
  let doneCount = 0;
  if (timetable?.today) {
    const now = lagosHour() * 60 + new Date().getMinutes();
    const done = timetable.completions?.[timetable.today.date] || [];
    todays = (timetable.blocks || []).filter((b) => b.day === timetable.today.day);
    doneCount = todays.filter((b) => done.includes(b.id)).length;
    next = todays.find((b) => toMin(b.end) > now && !done.includes(b.id));
  }

  return (
    <div className="dashboard-section ov-today">
      <div className="ov-today-plan">
        <h3>
          <CalendarDays size={18} /> Today
        </h3>
        {!timetable ? (
          <p className="ov-muted">Loading your plan…</p>
        ) : next ? (
          <div className="ov-next">
            <span className="ov-next-label">Next study session</span>
            <strong>{next.subject}</strong>
            <span>
              {fmt(next.start)} – {fmt(next.end)}
              {next.topic ? ` · ${next.topic}` : ""}
            </span>
          </div>
        ) : todays.length ? (
          <p className="ov-muted">
            {doneCount === todays.length
              ? `All ${todays.length} session${todays.length === 1 ? "" : "s"} done today — great work! 🎉`
              : "No more sessions planned for today."}
          </p>
        ) : (
          <p className="ov-muted">No study sessions planned for today.</p>
        )}
        <button type="button" className="ov-link" onClick={() => onNavigate("schedule")}>
          {timetable?.blocks?.length ? "Open my timetable →" : "Plan my reading week →"}
        </button>
        {courseContinue && (
          <button
            type="button"
            className="ov-link"
            onClick={() =>
              courseNavigate(
                `/courses/${courseContinue.slug}/${courseContinue.chapter_id}?class=${encodeURIComponent(courseContinue.class || "")}`
              )
            }
          >
            Continue: {courseContinue.subject} › {courseContinue.topic_title}
            {courseContinue.position ? ` · ${courseContinue.position}` : ""} →
          </button>
        )}
      </div>

      <div className="ov-quick">
        <span className="ov-quick-label">What would you like to do?</span>
        <div className="ov-quick-grid">
          <button type="button" className="ov-quick-btn" onClick={() => onAgentClick("AI Tutor")}>
            <Brain size={20} /> <span>Ask the AI tutor</span>
          </button>
          <button type="button" className="ov-quick-btn" onClick={() => onAgentClick("Quiz Generator")}>
            <Target size={20} /> <span>Take a quiz</span>
          </button>
          <button type="button" className="ov-quick-btn" onClick={() => onAgentClick("Summarizer")}>
            <FileText size={20} /> <span>Summarise my notes</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const StatsGrid = ({ progressStats, leaderboard }) => {
  const points = progressStats?.points || 0;
  const quizzes = progressStats?.completedQuizzes || 0;
  const avg = Math.round(progressStats?.averageScore || 0);
  const streak = progressStats?.streak || 0;
  const rank = leaderboard?.userRank;
  return (
    <div className="stats-grid ov-stats">
      <div className="stat-card">
        <div className="stat-icon lessons">
          <BookOpen />
        </div>
        <div className="stat-info">
          <h3>{quizzes}</h3>
          <p>{quizzes === 1 ? "Quiz completed" : "Quizzes completed"}</p>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon badges">
          <Target />
        </div>
        <div className="stat-info">
          <h3>{quizzes ? `${avg}%` : "—"}</h3>
          <p>{quizzes ? "Average quiz score" : "Average score (take a quiz!)"}</p>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon streak">
          <Zap />
        </div>
        <div className="stat-info">
          <h3>{points}</h3>
          <p>{points > 0 && rank ? `Points · #${rank} on the leaderboard` : "Points earned"}</p>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon rank">
          <Trophy />
        </div>
        <div className="stat-info">
          <h3>
            {streak} {streak === 1 ? "day" : "days"}
          </h3>
          <p>Learning streak</p>
        </div>
      </div>
    </div>
  );
};

const AgentsGrid = ({ onAgentClick }) => (
  <div className="dashboard-section agents-section ov-agents">
    <div className="ov-section-head">
      <h3>AI learning tools</h3>
      <span className="ov-muted">Pick a tool to start learning</span>
    </div>
    <div className="agents-grid ov-agents-grid">
      {AGENT_PAGES.map((agent) => (
        <button
          type="button"
          key={agent.name}
          className="agent-card ov-agent"
          onClick={() => onAgentClick(agent.name)}
        >
          <span className="agent-icon" style={{ backgroundColor: agent.color }}>
            <agent.icon />
          </span>
          <span className="agent-info">
            <h4>{agent.name}</h4>
            <p>{agent.description}</p>
          </span>
        </button>
      ))}
    </div>
  </div>
);

// Internal agent ids -> names students recognise
const AGENT_LABELS = {
  quizagent: "Quizzes",
  quiz: "Quizzes",
  quiz_agent: "Quizzes",
  summarizingagent: "Summaries",
  summarizing: "Summaries",
  summarizer: "Summaries",
  tutor: "AI Tutor",
  tutoragent: "AI Tutor",
  tutor_agent: "AI Tutor",
  feedbackagent: "Learning Insights",
  feedback: "Learning Insights",
  analyticsagent: "Analytics",
  analytics: "Analytics",
  progressmonitor: "Progress Monitor",
  progress: "Progress Monitor",
  recommendationagent: "Learning Path",
  recommendation: "Learning Path",
  curriculumviewer: "Curriculum Viewer",
  curriculum: "Curriculum Viewer",
  explanation: "Explanations",
};

const friendlyAgentName = (agent) =>
  AGENT_LABELS[String(agent).toLowerCase().replace(/[\s-]/g, "")] ||
  String(agent)
    .replace(/_?agent$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());

const AgentUsageStats = ({ agentMetrics }) => {
  if (!agentMetrics || agentMetrics.totalInteractions === 0) {
    return (
      <p className="no-data">
        Start interacting with our AI agents to see your learning statistics!
      </p>
    );
  }

  return (
    <div className="agent-usage-stats">
      <div className="usage-summary">
        <div className="usage-stat">
          <h4>{agentMetrics.totalInteractions}</h4>
          <p>Total Interactions</p>
        </div>
        <div className="usage-stat">
          <h4>{Math.round(agentMetrics.totalInteractionTime || 0)}</h4>
          <p>Minutes Spent Learning</p>
        </div>
        <div className="usage-stat">
          <h4>{agentMetrics.subjectsEngaged?.length || 0}</h4>
          <p>Subjects Explored</p>
        </div>
        <div className="usage-stat">
          <h4>{agentMetrics.topicsCovered?.length || 0}</h4>
          <p>Topics Covered</p>
        </div>
      </div>

      {agentMetrics?.agentUsageSummary &&
        Object.keys(agentMetrics.agentUsageSummary).length > 0 && (
          <div className="agent-usage-breakdown">
            <h4>Agent Usage</h4>
            <div className="usage-bars">
              {Object.entries(agentMetrics.agentUsageSummary).map(
                ([agent, count], index) => {
                  const maxCount = Math.max(
                    ...Object.values(agentMetrics.agentUsageSummary)
                  );
                  const percentage = Math.min(100, (count / maxCount) * 100);

                  return (
                    <div className="usage-bar-item" key={index}>
                      <div className="usage-label">
                        {friendlyAgentName(agent)}
                      </div>
                      <div className="usage-bar-container">
                        <div
                          className="usage-bar"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: `hsl(${index * 30}, 70%, 60%)`,
                          }}
                        />
                        <span className="usage-count">{count}</span>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}
    </div>
  );
};

// Turn raw activity records into readable sentences; points events and
// quiz results arrive mixed together from /profile/me/progress
const describeActivity = (a) => {
  const type = (a.type || "").toLowerCase();
  const subject = a.subject || "";
  const topic = a.topic && !/^(type|topic):|_/i.test(a.topic) ? a.topic : null;
  const score = typeof a.points === "number" ? a.points : null;
  if (type === "quiz activity") return null; // duplicate of the quiz result
  if (type.endsWith("quiz") && subject && subject !== "Quiz") {
    return { icon: Target, text: `Completed a ${subject} quiz${topic ? ` on ${topic}` : ""}`, badge: score != null ? `${Math.round(score)}%` : null, kind: "score" };
  }
  if (type.includes("tutor")) return { icon: MessageSquare, text: "Studied with the AI tutor", badge: score ? `+${score} pts` : null, kind: "points" };
  if (type.includes("summary") || type.includes("summar")) return { icon: FileText, text: "Summarised study notes", badge: score ? `+${score} pts` : null, kind: "points" };
  if (type.includes("explan")) return { icon: BookOpen, text: `Explored a concept${topic ? `: ${topic}` : ""}`, badge: score ? `+${score} pts` : null, kind: "points" };
  if (type.includes("welcome")) return { icon: Star, text: "Joined MyEduGenius — welcome!", badge: null };
  return { icon: BookOpen, text: a.description || a.type || "Learning activity", badge: null };
};

const RecentActivity = ({ activities }) => {
  const items = (activities || []).map((a) => ({ a, d: describeActivity(a) })).filter((x) => x.d).slice(0, 6);
  return (
    <div className="dashboard-section">
      <h3>Recent activity</h3>
      <div className="activity-list">
        {items.length ? (
          items.map(({ a, d }, index) => (
            <div key={index} className="activity-item">
              <div className="activity-icon">
                <d.icon size={16} />
              </div>
              <div className="activity-content">
                <p>{d.text}</p>
                <div className="activity-meta">
                  <span>{formatDate(a.timestamp)}</span>
                  {d.badge && <span className={`points ov-badge ${d.kind || ""}`}>{d.badge}</span>}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="no-data">Your learning activity will show up here.</p>
        )}
      </div>
    </div>
  );
};

const MiniLeaderboard = ({ leaderboard, onNavigate }) => {
  const rows = (leaderboard?.topStudents || []).slice(0, 5);
  return (
    <div className="dashboard-section ov-leader">
      <div className="ov-section-head">
        <h3>Leaderboard</h3>
        <button type="button" className="ov-link" onClick={() => onNavigate("leaderboard")}>
          See all →
        </button>
      </div>
      {rows.length ? (
        <ol className="ov-leader-list">
          {rows.map((r) => (
            <li key={`${r.rank}-${r.name}`} className={r.is_current_user ? "me" : ""}>
              <span className="ov-rank">#{r.rank}</span>
              <span className="ov-name">
                {r.name}
                {r.is_current_user ? " (you)" : ""}
              </span>
              <span className="ov-pts">{typeof r.points === "number" ? r.points : r.points?.total || 0} pts</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="no-data">No students on the leaderboard yet.</p>
      )}
    </div>
  );
};

// ==================== TAB COMPONENTS ====================
// What to work on next, built from the student's quizzes, course chapters and tutor questions
const FocusCard = () => {
  const { snapshot } = useLearnerSnapshot();
  const navigate = useNavigate();
  if (!snapshot || snapshot.empty || (!snapshot.focus?.length && !snapshot.strengths?.length && !snapshot.reviews_due?.length)) return null;
  const reviewPath = (r) =>
    r.kind === "chapter" && r.slug && r.chapter_id
      ? `/courses/${r.slug}/${r.chapter_id}?class=${encodeURIComponent(r.class || "")}&review=1`
      : `/agents/quiz?subject=${encodeURIComponent(r.subject || "")}&topic=${encodeURIComponent(r.topic || r.title || "")}`;
  const go = (path, f) => navigate(`${path}?subject=${encodeURIComponent(f.subject)}&topic=${encodeURIComponent(f.topic)}`);
  return (
    <div className="dashboard-section focus-card">
      <div className="section-header">
        <h3><Target size={18} aria-hidden="true" /> What to work on next</h3>
        <span className="focus-sub">Picked from your quizzes, course chapters and tutor chats</span>
      </div>
      {snapshot.reviews_due?.length > 0 && (
        <ul className="focus-list">
          {snapshot.reviews_due.map((r) => (
            <li key={`review-${r.slug || r.subject}-${r.chapter_id || r.topic || r.title}`} className="focus-item">
              <div className="focus-text">
                <strong>Quick review: {r.title}</strong>
                <span>{r.subject} · Learned earlier, a short check helps it stick</span>
              </div>
              <div className="focus-actions">
                <button type="button" className="eg-btn eg-btn-accent eg-btn-sm" onClick={() => navigate(reviewPath(r))}>Review now</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {snapshot.focus?.length > 0 ? (
        <ul className="focus-list">
          {snapshot.focus.map((f) => (
            <li key={`${f.kind}-${f.subject}-${f.topic}`} className="focus-item">
              <div className="focus-text">
                <strong>{f.topic}</strong>
                <span>{f.subject} · {f.reason}</span>
              </div>
              <div className="focus-actions">
                {f.kind === "weak" && (
                  <button type="button" className="eg-btn eg-btn-outline eg-btn-sm" onClick={() => go("/agents/quiz", f)}>Practise</button>
                )}
                <button type="button" className="eg-btn eg-btn-accent eg-btn-sm" onClick={() => go("/agents/tutor", f)}>Ask the tutor</button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="focus-sub">Nothing to catch up on. Keep going with your courses!</p>
      )}
      {snapshot.strengths?.length > 0 && (
        <p className="focus-strengths">
          <Star size={14} aria-hidden="true" /> Going well: {snapshot.strengths.map((s) => `${s.topic} (${s.subject})`).join(", ")}
        </p>
      )}
    </div>
  );
};

const OverviewTab = ({ dashboardData, onAgentClick, onNavigate }) => (
  <>
    <WelcomeSection studentProfile={dashboardData.studentProfile} progressStats={dashboardData.progressStats} />

    <TodayCard onNavigate={onNavigate} onAgentClick={onAgentClick} />

    <FocusCard />

    <StatsGrid progressStats={dashboardData.progressStats} leaderboard={dashboardData.leaderboard} />

    <AgentsGrid onAgentClick={onAgentClick} />

    <div className="dashboard-grid ov-bottom">
      <RecentActivity activities={dashboardData.recentActivity} />
      <MiniLeaderboard leaderboard={dashboardData.leaderboard} onNavigate={onNavigate} />
    </div>
  </>
);

const ProgressTab = ({ dashboardData, onAgentClick }) => {
  const stats = dashboardData.progressStats || {};
  const quizzes = stats.completedQuizzes || 0;
  return (
    <>
      <div className="section-header">
        <h2>Learning progress</h2>
      </div>

      <div className="stats-grid ov-stats">
        <div className="stat-card">
          <div className="stat-icon lessons">
            <Target />
          </div>
          <div className="stat-info">
            <h3>{quizzes ? `${Math.round(stats.averageScore || 0)}%` : "—"}</h3>
            <p>Average quiz score</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon badges">
            <BookOpen />
          </div>
          <div className="stat-info">
            <h3>{quizzes}</h3>
            <p>{quizzes === 1 ? "Quiz completed" : "Quizzes completed"}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon streak">
            <Star />
          </div>
          <div className="stat-info">
            <h3>Level {stats.level || 1}</h3>
            <p>{100 - (stats.xpProgress || 0)} points to the next level</p>
            <div className="progress-bar">
              <div className="progress" style={{ width: `${stats.xpPercentage || 0}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <h3>🏆 Points breakdown</h3>
        <PointsWidget pointsData={dashboardData.pointsData} />
      </div>

      <LearningStreak userId={dashboardData.studentProfile.studentId || dashboardData.studentProfile.userId} />

      <div className="dashboard-grid">
        <div className="dashboard-section">
          <h3>How you've been learning</h3>
          <AgentUsageStats agentMetrics={dashboardData.agentMetrics} />
        </div>
        <div className="dashboard-section ov-deeper">
          <h3>Go deeper</h3>
          <p className="ov-muted">See detailed charts, topic scores and personalised next steps.</p>
          <div className="ov-deeper-actions">
            <button type="button" className="eg-btn eg-btn-primary" onClick={() => onAgentClick("Progress Monitor")}>
              Progress monitor
            </button>
            <button type="button" className="eg-btn eg-btn-outline" onClick={() => onAgentClick("Analytics")}>
              Analytics
            </button>
            <button type="button" className="eg-btn eg-btn-outline" onClick={() => onAgentClick("Learning Path")}>
              My learning path
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const MessagesTab = ({ studentProfile }) => (
  <>
    <div className="section-header">
      <h2>Messages</h2>
      <p className="section-subtitle">
        Connect with your teachers in real-time
      </p>
    </div>

    <Messaging
      currentUserId={studentProfile.studentId}
      currentUserType="student"
      currentUserName={studentProfile.name}
    />
  </>
);

const ScheduleTab = () => (
  <>
    <div className="section-header">
      <h2>Reading Timetable</h2>
    </div>
    <ReadingTimetable />
  </>
);

const PERIODS = [
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "all_time", label: "All Time" },
];

const MEDALS = { 1: "#D9A21B", 2: "#9AA4B2", 3: "#B87333" };

// Badges may arrive as strings or objects ({ name, description, ... })
const badgeLabel = (badge) =>
  typeof badge === "string" ? badge : badge?.name || badge?.title || badge?.badge_name || "Badge";

const LeaderboardTab = ({ leaderboard, achievements }) => {
  const [period, setPeriod] = useState("all_time");
  const [board, setBoard] = useState({
    entries: leaderboard?.topStudents || [],
    rank: leaderboard?.userRank || null,
    total: leaderboard?.totalStudents || 0,
    points: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
        const res = await fetch(`${API_BASE_URL}/points/leaderboard?limit=20&period=${period}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setBoard({
            entries: data.leaderboard || [],
            rank: data.current_user_rank,
            total: data.pagination?.total_count || 0,
            points: data.current_user_points,
          });
        }
      } catch {
        if (!cancelled) setError("Couldn't load the leaderboard. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const badges = achievements?.badges || [];
  const periodLabel = PERIODS.find((p) => p.id === period)?.label.toLowerCase();

  return (
    <>
      <div className="section-header">
        <h2>Leaderboard</h2>
        <div className="leaderboard-filters" role="tablist" aria-label="Leaderboard period">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={period === p.id}
              className={`filter-btn ${period === p.id ? "active" : ""}`}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {board.rank && (
        <div className="dashboard-section leaderboard-you">
          <Trophy size={22} />
          <div>
            <strong>
              You&apos;re #{board.rank} of {board.total}
            </strong>
            <span>
              {board.points ?? 0} points {period === "all_time" ? "in total" : periodLabel}
            </span>
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        <div className="dashboard-section">
          <h3>Top Students</h3>
          {error ? (
            <p className="no-data">{error}</p>
          ) : loading && board.entries.length === 0 ? (
            <p className="no-data">Loading…</p>
          ) : board.entries.length === 0 ? (
            <p className="no-data">No students on the leaderboard yet.</p>
          ) : (
            <div className={`activity-list ${loading ? "is-loading" : ""}`}>
              {board.entries.map((student) => (
                <div
                  key={`${student.rank}-${student.name}`}
                  className={`activity-item leaderboard-row ${student.is_current_user ? "current-user" : ""}`}
                >
                  <div className="activity-icon">
                    <span style={{ fontWeight: 700 }}>#{student.rank}</span>
                  </div>
                  <div className="activity-content">
                    <p style={{ fontWeight: 600 }}>
                      {student.name}
                      {student.is_current_user ? " (you)" : ""}
                    </p>
                    <div className="activity-meta">
                      <span>
                        {typeof student.points === "number" ? student.points : student.points?.total ?? 0} points
                      </span>
                      {student.rank <= 3 && student.points > 0 && (
                        <Trophy size={16} style={{ color: MEDALS[student.rank] }} aria-label={`Rank ${student.rank}`} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-section">
          <h3>Your Achievements</h3>
          <div className="activity-list">
            {badges.length > 0 ? (
              badges.map((badge, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">
                    <Award size={20} />
                  </div>
                  <div className="activity-content">
                    <p>{badgeLabel(badge)}</p>
                    {typeof badge === "object" && badge?.description && (
                      <div className="activity-meta">
                        <span>{badge.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-data">Complete more activities to earn badges!</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const ProfileTab = ({
  studentProfile,
  progressStats,
  achievements,
  navigate,
}) => (
  <>
    <div className="section-header">
      <h2>Profile</h2>
      <button
        className="secondary-btn"
        onClick={() => navigate("/student/profile/edit")}
      >
        <Settings size={16} />
        Edit Profile
      </button>
    </div>

    <div className="dashboard-section">
      <div className="welcome-section">
        <div
          className="student-avatar"
          style={{ width: 80, height: 80, fontSize: 24 }}
        >
          {studentProfile.name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()}
        </div>
        <div className="welcome-text">
          <h2>{studentProfile.name}</h2>
          <p>{studentProfile.grade}</p>
        </div>
        <div className="level-info">
          <div className="level-badge">Level {progressStats.level}</div>
          <div className="points-display">
            <Star />
            <span>{progressStats.points} Points</span>
          </div>
          <div className="points-display">
            <Trophy />
            <span>{progressStats.streak} Day Streak</span>
          </div>
        </div>
      </div>
    </div>

    <div className="dashboard-grid">
      <div className="dashboard-section">
        <h3>Personal Information</h3>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-content">
              <p style={{ fontWeight: 600 }}>Email</p>
              <p>{studentProfile.email || "Not provided"}</p>
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-content">
              <p style={{ fontWeight: 600 }}>Student ID</p>
              <p>{studentProfile.studentId || "Not provided"}</p>
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-content">
              <p style={{ fontWeight: 600 }}>School</p>
              <p>{studentProfile.school || "Not provided"}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <h3>Learning Preferences</h3>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-content">
              <p style={{ fontWeight: 600 }}>Learning Style</p>
              <p>{studentProfile.learningStyle || "Not set"}</p>
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-content">
              <p style={{ fontWeight: 600 }}>Preferred Time</p>
              <p>{studentProfile.preferredTime || "Not set"}</p>
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-content">
              <p style={{ fontWeight: 600 }}>Notifications</p>
              <p>{studentProfile.notifications || "Email + Push"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="dashboard-section">
      <h3>Achievements</h3>
      <div className="activity-list">
        {achievements.badges && achievements.badges.length > 0 ? (
          achievements.badges.map((badge, index) => (
            <div key={index} className="activity-item">
              <div className="activity-icon">
                <Award size={20} />
              </div>
              <div className="activity-content">
                <p>{badge}</p>
              </div>
            </div>
          ))
        ) : (
          <p className="no-data">
            No achievements yet. Keep learning to earn badges!
          </p>
        )}
      </div>
    </div>
  </>
);

// ==================== MAIN COMPONENT ====================
const StudentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { data: dashboardData, loading, error, refetch } = useDashboardData();
  const refetchTimeoutRef = React.useRef(null);

  // Debounced refetch to prevent excessive calls
  const debouncedRefetch = useCallback(
    (delay = 0) => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      refetchTimeoutRef.current = setTimeout(() => {
        refetch();
        refetchTimeoutRef.current = null;
      }, delay);
    },
    [refetch]
  );

  // Initial fetch and refresh
  useEffect(() => {
    // Let the first periodic interval fetch handle initial load
    // This prevents double-fetching on mount
    debouncedRefetch(500);

    // Check for refresh triggers
    if (location.state?.profileUpdated) {
      window.history.replaceState({}, document.title);
    }

    const needsRefresh = localStorage.getItem("dashboardNeedsRefresh");
    if (needsRefresh === "true") {
      console.log("🔄 Dashboard detected refresh flag, triggering refresh");
      localStorage.removeItem("dashboardNeedsRefresh");
      // Trigger immediate refresh when flag is detected
      debouncedRefetch(1000);
    }

    // Periodic refresh
    const interval = setInterval(() => debouncedRefetch(0), REFRESH_INTERVAL);
    return () => {
      clearInterval(interval);
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
    };
  }, [debouncedRefetch, location.state?.profileUpdated]);

  // Storage event listener for cross-tab communication
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "dashboardNeedsRefresh" && e.newValue === "true") {
        console.log("📡 Storage event detected - cross-tab dashboard refresh");
        localStorage.removeItem("dashboardNeedsRefresh");
        debouncedRefetch(1000);
      }
    };

    // Also check localStorage periodically for missed events
    const checkRefreshFlag = () => {
      const needsRefresh = localStorage.getItem("dashboardNeedsRefresh");
      if (needsRefresh === "true") {
        console.log("🔍 Periodic check found refresh flag");
        localStorage.removeItem("dashboardNeedsRefresh");
        debouncedRefetch(500);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Check for refresh flag every 60 seconds as fallback
    const intervalCheck = setInterval(
      checkRefreshFlag,
      60000 // 60 seconds
    );

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalCheck);
    };
  }, [debouncedRefetch]);

  // Quiz completion event listener
  useEffect(() => {
    const handleQuizCompletion = () => {
      console.log("🎯 Dashboard received quiz completion event");
      // Add a delay to ensure backend data is saved and processed
      debouncedRefetch(3000);
    };

    const handleDashboardRefreshNeeded = (event) => {
      console.log("🔥 Dashboard refresh needed event received:", event.detail);
      // Shorter delay for immediate refresh event
      debouncedRefetch(1500);
    };

    window.addEventListener("quizCompleted", handleQuizCompletion);
    window.addEventListener(
      "dashboardRefreshNeeded",
      handleDashboardRefreshNeeded
    );

    return () => {
      window.removeEventListener("quizCompleted", handleQuizCompletion);
      window.removeEventListener(
        "dashboardRefreshNeeded",
        handleDashboardRefreshNeeded
      );
    };
  }, [debouncedRefetch]);

  // User activity tracking for session management
  useEffect(() => {
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleActivity = () => {
      trackActivity();
    };

    // Throttle activity tracking to prevent excessive calls
    let activityTimeout = null;
    const throttledActivityHandler = () => {
      if (activityTimeout) return;

      activityTimeout = setTimeout(() => {
        handleActivity();
        activityTimeout = null;
      }, 10000); // Track activity at most once every 10 seconds
    };

    // Add event listeners for user activity
    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledActivityHandler, true);
    });

    // Initialize session timeout
    resetSessionTimeout();

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledActivityHandler, true);
      });
      if (sessionTimeoutId) {
        clearTimeout(sessionTimeoutId);
      }
    };
  }, []);

  const handleAgentClick = useCallback(
    (agentName) => {
      const agent = AGENT_PAGES.find((a) => a.name === agentName);
      if (agent?.route) {
        navigate(agent.route, {
          state: {
            userType: "student",
            context: {
              studentId: dashboardData?.studentProfile?.studentId,
              userId: dashboardData?.studentProfile?.userId,
              subject: dashboardData?.studentProfile?.currentSubject,
              grade: dashboardData?.studentProfile?.grade,
            },
          },
        });
      }
    },
    [navigate, dashboardData]
  );

  const handleNavClick = useCallback((tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="student-dashboard">
        <div className="loading-screen">
          <Loader className="loading-spinner" size={48} />
          <h3>Loading your dashboard...</h3>
          <p>Gathering your latest learning progress</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="student-dashboard">
        <div className="error-screen">
          <AlertCircle size={48} color="#ef4444" />
          <h3>Unable to load dashboard</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => debouncedRefetch(0)}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  return (
    <Layout>
      <div className="student-dashboard">
        {/* Sidebar Overlay */}
        <div
          className={`sidebar-overlay ${sidebarOpen ? "show" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sidebar-header">
            <div className="sidebar-header-top">
              <h2>Student Portal</h2>
              <button
                className="sidebar-close-btn"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close sidebar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="student-info">
              <div className="student-avatar">
                {dashboardData.studentProfile.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()}
              </div>
              <div>
                <h4>{dashboardData.studentProfile.name}</h4>
                <p>{dashboardData.studentProfile.grade}</p>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => handleNavClick("overview")}
            >
              <BarChart3 size={20} />
              <span>Overview</span>
            </button>
            <button className="nav-item" onClick={() => navigate("/courses")}>
              <BookOpen size={20} />
              <span>Courses</span>
            </button>
            <button
              className={`nav-item ${activeTab === "progress" ? "active" : ""}`}
              onClick={() => handleNavClick("progress")}
            >
              <TrendingUp size={20} />
              <span>Progress</span>
            </button>
            <button
              className={`nav-item ${activeTab === "messages" ? "active" : ""}`}
              onClick={() => handleNavClick("messages")}
            >
              <MessageSquare size={20} />
              <span>Messages</span>
              {dashboardData.messages.unreadCount > 0 && (
                <span className="notification-badge">
                  {dashboardData.messages.unreadCount}
                </span>
              )}
            </button>
            <button
              className={`nav-item ${activeTab === "schedule" ? "active" : ""}`}
              onClick={() => handleNavClick("schedule")}
            >
              <Calendar size={20} />
              <span>Timetable</span>
            </button>
            <button
              className={`nav-item ${
                activeTab === "leaderboard" ? "active" : ""
              }`}
              onClick={() => handleNavClick("leaderboard")}
            >
              <Trophy size={20} />
              <span>Leaderboard</span>
            </button>
            <button
              className={`nav-item ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => handleNavClick("profile")}
            >
              <User size={20} />
              <span>Profile</span>
            </button>
          </nav>

          <div className="sidebar-footer">
            <button
              className="nav-item"
              onClick={() =>
                navigate("/student/profile/edit", {
                  state: { returnPath: "/student-dashboard" },
                })
              }
            >
              <Settings size={20} />
              <span>Settings</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Phones only: a button to open the menu. (The search box and bell did nothing; Settings is in the sidebar.) */}
          <div className="top-bar">
            <button
              className="icon-btn mobile-menu-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              <span>Menu</span>
            </button>
          </div>

          <div className="dashboard-content">
            <AnnouncementBanner />
            {activeTab === "overview" && (
              <OverviewTab
                dashboardData={dashboardData}
                onAgentClick={handleAgentClick}
                onNavigate={handleNavClick}
              />
            )}

            {activeTab === "progress" && (
              <ProgressTab dashboardData={dashboardData} onAgentClick={handleAgentClick} />
            )}

            {activeTab === "messages" && (
              <MessagesTab studentProfile={dashboardData.studentProfile} />
            )}

            {activeTab === "schedule" && (
              <ScheduleTab />
            )}

            {activeTab === "leaderboard" && (
              <LeaderboardTab
                leaderboard={dashboardData.leaderboard}
                achievements={dashboardData.achievements}
              />
            )}

            {activeTab === "profile" && (
              <ProfileTab
                studentProfile={dashboardData.studentProfile}
                progressStats={dashboardData.progressStats}
                achievements={dashboardData.achievements}
                navigate={navigate}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentDashboard;
